import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useInSeasonLocalStorage } from '@sorare/core/src/hooks/search/useInSeasonLocalStorage';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';

import { BackendMarketSearchLayout } from '#src/components/market/BackendMarketSearchLayout/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { useBackendMarketCardsSearch } from '#src/hooks/search/useBackendMarketCardsSearch.ts';
import {
  MARKET_CARDS_SEARCH_QUERY,
  MARKET_GROUPED_CARDS_SEARCH_QUERY,
} from '#src/hooks/search/useMarketCardsSearch.ts';
import { useDefaultFilters } from '#src/hooks/useDefaultFilters.ts';
import { messages as searchLayoutMessages } from '#src/searchCards/AdvancedCardSearch/SearchLayout.tsx';
import { BackendCardResultsFromGraphQL } from '#src/searchCards/BackendCardResultsFromGraphQL/index.tsx';
import { BackendStackedSwitch } from '#src/searchCards/StackedSwitch/BackendStackedSwitch.tsx';
import { backendSecondaryMarketFilters as footballBackendSecondaryMarketFilters } from '#src/searchCards/football/cardFilters.tsx';
import { backendSecondaryMarketFilters as mlbBackendSecondaryMarketFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { backendSecondaryMarketFilters as nbaBackendSecondaryMarketFilters } from '#src/searchCards/nba/cardFilters.tsx';
import { startingPredictionSortFor } from '#src/searchCards/startingPredictionSort.ts';

const managerSalesSorts = (sport: Sport): GallerySortNames => [
  'Cards On Sale Newly Listed',
  'Cards On Sale Best Value',
  'Cards On Sale Popular Player',
  'Cards On Sale Lowest Price',
  'Cards On Sale Highest Average Score',
  ...startingPredictionSortFor(sport),
];

const STACKED_DEFAULT_SORT: GallerySortName = 'Cards On Sale Popular Player';
const UNSTACKED_DEFAULT_SORT: GallerySortName = 'Cards On Sale Newly Listed';

const PAGE_SIZE = 40;

const backendSecondaryMarketFiltersBySport = {
  [Sport.FOOTBALL]: footballBackendSecondaryMarketFilters,
  [Sport.NBA]: nbaBackendSecondaryMarketFilters,
  [Sport.BASEBALL]: mlbBackendSecondaryMarketFilters,
};

type Props = {
  sport: Sport;
  banner?: ReactNode;
};

const BackendManagerSalesSearchContent = ({ sport, banner }: Props) => {
  const { formatMessage } = useIntl();

  const unstacked = !!useQueryString(SEARCH_PARAMS.UNSTACKED);
  const stacked = !unstacked;

  const cardFilters =
    backendSecondaryMarketFiltersBySport[
      sport as keyof typeof backendSecondaryMarketFiltersBySport
    ]?.() ?? [];

  const defaultFilters = useDefaultFilters({ secondary: true });

  const {
    query,
    page,
    refinements,
    facets,
    sortInputs,
    advancedFilters,
    filtersLoading,
    setSearchParams,
    searchParams,
    makeGqlSearchContextValue,
  } = useBackendMarketCardsSearch({
    sport,
    cardFilters,
    defaultFilters,
    defaultSort: stacked ? STACKED_DEFAULT_SORT : UNSTACKED_DEFAULT_SORT,
  });

  const [inSeason] = useInSeasonLocalStorage();
  const inSeasonDefaultApplied = useRef(false);
  useEffect(() => {
    if (inSeasonDefaultApplied.current) return;
    inSeasonDefaultApplied.current = true;
    if (stacked && inSeason && !searchParams.has(SEARCH_PARAMS.IN_SEASON)) {
      setSearchParams(
        params => {
          params.set(SEARCH_PARAMS.IN_SEASON, 'true');
          return params;
        },
        { replace: true }
      );
    }
  }, [stacked, inSeason, searchParams, setSearchParams]);

  const commonVariables = {
    query,
    page,
    pageSize: PAGE_SIZE,
    advancedFilters,
    sorts: sortInputs,
    refinements,
    facets,
    onSaleOnly: true,
  };

  const flat = useQuery(MARKET_CARDS_SEARCH_QUERY, {
    skip: filtersLoading || stacked,
    variables: commonVariables,
  });
  const grouped = useQuery(MARKET_GROUPED_CARDS_SEARCH_QUERY, {
    skip: filtersLoading || !stacked,
    variables: commonVariables,
  });

  const flatResult = (flat.data ?? flat.previousData)?.searchCards;
  const groupedResult = (grouped.data ?? grouped.previousData)
    ?.searchGroupedCards;

  const searchResult = stacked ? groupedResult : flatResult;
  const loading = stacked ? grouped.loading : flat.loading;

  const groups = groupedResult?.groups ?? [];
  const hitSlugs = stacked
    ? (groups.map(g => g.hits[0]?.slug).filter(Boolean) as string[])
    : (flatResult?.hits?.map(h => h.slug) ?? []);

  const stacksBySlug = stacked
    ? Object.fromEntries(
        groups.flatMap(g => {
          const representative = g.hits[0];
          if (!representative) return [];
          return [
            [
              representative.slug,
              {
                count: g.stackSize ?? 1,
                distinctKey: representative.sale?.distinctKey ?? null,
              },
            ],
          ];
        })
      )
    : undefined;

  return (
    <GQLSearchContext value={makeGqlSearchContextValue(searchResult)}>
      <BackendMarketSearchLayout
        banner={banner}
        cardFilters={cardFilters}
        sorts={managerSalesSorts(sport)}
        searchBoxPlaceholder={formatMessage(
          searchLayoutMessages.defaultPlaceholder
        )}
        sortRowExtra={<BackendStackedSwitch />}
      >
        <BackendCardResultsFromGraphQL
          hitSlugs={hitSlugs}
          stacksBySlug={stacksBySlug}
          searchLoading={loading || filtersLoading}
          nbHits={searchResult?.nbHits ?? 0}
          sport={sport}
          showDesktopFilter
        />
      </BackendMarketSearchLayout>
    </GQLSearchContext>
  );
};

export const supportsBackendManagerSalesSearch = (sport: Sport) =>
  sport in backendSecondaryMarketFiltersBySport;

export const BackendManagerSalesSearch = ({ sport, banner }: Props) => {
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  if (!supportsBackendManagerSalesSearch(sport)) return null;

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{ sport }}
    >
      <SearchCardsContextProvider commonMarket={false}>
        <BackendManagerSalesSearchContent sport={sport} banner={banner} />
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
